import { formatDateTime } from "@dofek/format/format";
import { deviceDisplayName } from "./device-names.ts";
import { PROVIDER_CATALOG, providerCatalogEntry } from "./provider-catalog.ts";

/** Display labels for provider IDs, shared across web and iOS. */
export const PROVIDER_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(PROVIDER_CATALOG).map(([id, entry]) => [id, entry.label]),
);

/** Human-readable label for a provider ID, falling back to the raw ID. */
export function providerLabel(id: string): string {
  return providerCatalogEntry(id)?.label ?? id;
}

export function providerSourceLabel(id: string, subsource?: string | null): string {
  if ((id === "apple_health" || id === "apple-health") && subsource) {
    return `${deviceDisplayName(subsource)} (via Apple Health)`;
  }
  if (id === "garmin") return providerRecordLabel(id, subsource);
  return providerLabel(id);
}

export function providerRecordLabel(id: string, sourceName?: string | null): string {
  const label = providerLabel(id);
  const source = sourceName?.trim();
  if (!source) return label;
  const normalizedSource = source.toLowerCase();
  if (normalizedSource === id.toLowerCase() || normalizedSource === label.toLowerCase()) {
    return label;
  }
  return `${label} · ${deviceDisplayName(source)}`;
}

export interface ProviderAbsentSource {
  providerId: string;
  providerAbsentAt: string | null;
  subsource?: string | null;
}

export function formatProviderAbsentTombstoneSummary(
  providerId: string,
  providerAbsentAt: string,
  subsource?: string | null,
): string {
  return `Removed from ${providerSourceLabel(providerId, subsource)} · ${formatDateTime(providerAbsentAt)}`;
}

export function formatProviderPartialAbsenceSummary(
  sources: ProviderAbsentSource[],
): string | null {
  if (sources.length === 0) return null;
  return sources
    .map((source) => {
      const removedAt = source.providerAbsentAt
        ? ` · ${formatDateTime(source.providerAbsentAt)}`
        : "";
      return `${providerSourceLabel(source.providerId, source.subsource)} removed${removedAt}`;
    })
    .join(", ");
}

export function providerAbsentExplanation(id: string, subsource?: string | null): string {
  if (id === "apple_health" && subsource) {
    const source = deviceDisplayName(subsource);
    return `The Apple Health copy of this workout (originally from ${source}) was removed from sync. This does not mean ${source} deleted the activity.`;
  }
  return `This activity was hidden because ${providerSourceLabel(id, subsource)} reported it as deleted or missing.`;
}

export const BRAND_COLORS: Readonly<Record<string, string>> = Object.entries(
  PROVIDER_CATALOG,
).reduce<Record<string, string>>((colors, [id, entry]) => {
  if (entry.brandColor) colors[id] = entry.brandColor;
  return colors;
}, {});

export function providerLogoType(id: string): "svg" | "png" | null {
  return providerCatalogEntry(id)?.logo?.type ?? null;
}

export function providerLogoId(id: string): string {
  return providerCatalogEntry(id)?.logo?.id ?? id;
}

/** Resolve the recording app separately from its Apple Health transport. */
export function providerSourceLogo(id: string, sourceName?: string | null) {
  if (id !== "apple_health" && id !== "apple-health") {
    return { provider: id, viaAppleHealth: false };
  }
  const source = sourceName?.trim();
  const normalized = source?.toLowerCase();
  if (!source || normalized === "apple health" || normalized === "healthkit") {
    return { provider: "apple_health", viaAppleHealth: false };
  }
  if (/(?:^|\s)apple\s+watch$/i.test(source)) {
    return { provider: "apple-watch", viaAppleHealth: true };
  }
  const match = Object.entries(PROVIDER_CATALOG).find(
    ([provider, entry]) => provider === normalized || entry.label.toLowerCase() === normalized,
  );
  const provider = normalized === "hang ten training" ? "hang-ten" : (match?.[0] ?? source);
  return { provider, viaAppleHealth: true };
}
